import React, { useEffect, useRef, useState } from "react";
import { Link2, X } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import {
  appLinksNeedAction, appLinksSupported, dismissAppLinksNudge, getAppLinkState, isAppLinksNudgeDismissed, isAppLinksProven,
  markAppLinksProven, onAppLinksEnabled, openAppLinkSettings, shouldShowAppLinksNudge,
} from "@/lib/appLinks";

// Bandeau fin « Ouvre les liens yalola.app ici » — accueil (Android 12+, app native) quand le téléphone n'a pas
// vérifié yalola.app tout seul (Xiaomi / HyperOS…). Un tap sur « Activer » ouvre l'écran système ; au retour :
// réglé → bandeau fermé pour de bon (preuve) ; sinon une seule aide (toast). Masqué 30 j après ✕.
export const AppLinksNudgeCard = () => {
  const { t } = useApp();
  const [show, setShow] = useState(false);
  const [awaiting, setAwaiting] = useState(false);
  const helped = useRef(false);
  const finished = useRef(false);

  useEffect(() => {
    if (!appLinksSupported() || isAppLinksNudgeDismissed() || isAppLinksProven()) return;
    let alive = true;
    getAppLinkState().then((s) => { if (alive && shouldShowAppLinksNudge(s)) setShow(true); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!awaiting) return;
    const done = () => {
      if (finished.current) return;
      finished.current = true;
      markAppLinksProven();
      setShow(false);
      toast.success(t("applinks.done", "C'est réglé : les liens yalola.app s'ouvriront dans l'app."));
    };
    const check = () => {
      if (document.hidden || finished.current) return;
      getAppLinkState().then((s) => {
        if (finished.current) return;
        if (appLinksNeedAction(s)) {
          if (!helped.current) {
            helped.current = true;
            toast(t("applinks.help", "Active « Ouvrir les liens compatibles » puis reviens"));
          }
          return;
        }
        done();
      });
    };
    document.addEventListener("visibilitychange", check);
    // Retour automatique : le plugin ramène l'app dès que l'interrupteur est activé.
    const off = onAppLinksEnabled(done);
    return () => { document.removeEventListener("visibilitychange", check); off(); };
  }, [awaiting, t]);

  if (!show) return null;
  const close = () => { dismissAppLinksNudge(); setShow(false); };
  const open = () => { setAwaiting(true); openAppLinkSettings().catch(() => {}); };

  return (
    <div className="yalola-card pl-3 pr-2 py-2 flex items-center gap-2.5 animate-in fade-in slide-in-from-top-2 duration-500" data-testid="applinks-nudge-card">
      <span className="h-8 w-8 shrink-0 rounded-full grid place-items-center" style={{ background: "var(--yalola-primary-soft)" }}>
        <Link2 size={15} style={{ color: "var(--yalola-primary)" }} />
      </span>
      <span className="flex-1 min-w-0 flex flex-col gap-0.5">
        <span className="text-[13px] font-semibold leading-tight line-clamp-2" style={{ color: "var(--yalola-text)" }} data-testid="applinks-nudge-text">
          {t("applinks.strip", "Ouvre les liens yalola.app ici")}
        </span>
        <span className="text-[11px] leading-tight line-clamp-2" style={{ color: "var(--yalola-text-muted)" }} data-testid="applinks-nudge-hint">
          {t("applinks.strip_hint", "Si un lien Yalola ne s'ouvre pas dans l'app, touche Activer.")}
        </span>
      </span>
      <Button size="sm" className="rounded-full h-8 px-3.5 shrink-0" onClick={open} data-testid="applinks-nudge-open">
        {t("applinks.enable", "Activer")}
      </Button>
      <button type="button" onClick={close} className="p-1.5 rounded-full hover:opacity-70 shrink-0" aria-label={t("applinks.later", "Plus tard")} data-testid="applinks-nudge-close">
        <X size={16} style={{ color: "var(--yalola-text-muted)" }} />
      </button>
    </div>
  );
};
